var ScriptTypes;
(function (ScriptTypes) {
    ScriptTypes["JAVASCRIPT"] = "application/javascript";
    ScriptTypes["PLAIN"] = "text/plain";
})(ScriptTypes || (ScriptTypes = {}));

const getScript$1 = (googleAnalyticsId) => `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  // Consent mode
  gtag('consent', 'default', {
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'analytics_storage': 'denied'
  });

  (function(w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', '${googleAnalyticsId}');`;
/**
 * Note: Attributes type="text/plain" and data-usercentrics="Google Tag Manager" are added
 * to the Google Tag Manager script, in order for the script to only be
 * activated once consent for Google Tag Manager is given.
 * @param gtm
 * @returns Google Tag Manager script
 *
 * Google Tag Manager is a tag management system that acts as common tool for running all different tracking tags
 * such as Google Ads, Google Analytics, Facebook Pixel, Linkedln tags through
 * the single central interface of Google Tag Manager.
 */
const gtmObject = (gtm) => {
    return {
        innerHTML: getScript$1(gtm.analyticsId),
        type: 'text/plain',
        'data-usercentrics': gtm['data-test-id'] ?? 'Google Tag Manager',
        defer: gtm.defer ?? true,
        async: gtm.async ?? true,
        id: gtm.id ?? 'gtm-cmp',
    };
};

const getScript = (googleAnalyticsId, googleOptimizeId) => {
    return `
    window.dataLayer = window.dataLayer || []
    function gtag(){ dataLayer.push(arguments) }
    gtag('js', new Date())
    gtag('config', '${googleAnalyticsId}', { 'optimize_id': '${googleOptimizeId}' })
    gtag('consent', 'default', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied'
    })`;
};
/**
 * Note: Attributes type="text/plain" and data-usercentrics="Google Tag Manager" are added
 * to the gtag script, in order for the script to only be
 * activated once consent for Google Tag Manager is given.
 * @param gtag
 * @returns Global site tag (Gtag) script
 *
 * Global site tag is a tag management framework that acts as a common tool for running only Google tracking tools
 * such as Google Analytics, Google optimize etc.
 */
const gtagObject = (gtag) => {
    return {
        innerHTML: getScript(gtag.analyticsId, gtag.optimizeId),
        type: 'text/plain',
        'data-usercentrics': gtag['data-test-id'] ?? 'Google Tag Manager',
        id: gtag.id ?? 'gtag-cmp',
    };
};

/**
 *
 * @param uc
 * @returns Returns CMP Script Tag to easily control scripts implemented on the website and manage consent settings of the users.
 */
const script = (uc) => {
    return {
        src: 'https://web.cmp.usercentrics.eu/ui/loader.js',
        id: uc.id ?? 'usercentrics-cmp',
        type: 'application/javascript',
        'data-settings-id': uc.ucId,
        defer: uc.defer ?? true,
        async: uc.async ?? true,
        mode: 'client',
    };
};

/**
 * Build a list of scripts containing usercentrics, gtm, gtag and other tracking scripts configured by the user.
 * Connect them to usercentrics while making sure they don't execute until consent is given.
 */
class Tracking {
    customEvents = [];
    /** Order Matters
     * The Usercentrics script has to be inserted BEFORE the Google Tag Manager script or Gtag Script
     * @param trackingConfigs
     * @returns array of scripts that can be used with vue-meta to load scripts
     */
    toObjects(trackingConfigs) {
        return [
            this.buildUsercentricsScript(trackingConfigs.usercentrics),
            this.buildGTMScript(trackingConfigs?.gtm),
            this.buildGtagScript(trackingConfigs?.gtag),
            ...this.buildOtherScripts(trackingConfigs?.scripts),
        ].filter(Boolean);
    }
    /**
     * Order Matters
     * The Usercentrics script has to be inserted BEFORE the Google Tag Manager script or Gtag Script
     * @param trackingConfigs
     * @returns Scripts as HTML Strings
     */
    toHTML(trackingConfigs) {
        return this.toObjects(trackingConfigs).map(this.scriptToHTML);
    }
    /**
     * When a container is loaded, Tag Manager will begin to process all queued data layer push messages.
     * Tag Manager processes messages on a first-in, first-out basis: Each message is processed one at a time, in the order it was received.
     * If the message is an event, any tags with triggers conditions that have been met will fire before Tag Manager moves on to the next message.
     * @param data can be used to pass any information to tags.
     * Events or variables can be passed via the data layer, and triggers can be set up based on the values of variables.
     * https://developers.google.com/tag-platform/tag-manager/web/datalayer
     */
    pushToDataLayer(data) {
        if (this.isEmptyObject(data)) {
            return;
        }
        if (window && window['dataLayer']) {
            this.sendPendingCustomEventsToDatalayer();
            window['dataLayer'].push(data);
        }
        else {
            this.customEvents.push(data);
        }
    }
    /**
     *
     * @param script
     * @returns HTML string for a given script
     */
    scriptToHTML(script) {
        const scriptAttributesString = Object.entries(script)
            .filter(([, value]) => Boolean(value))
            .reduce((html, [key, value]) => {
            return html + `${key}="${value}" `;
        }, '');
        return `<script ${scriptAttributesString} >${script.innerHTML ?? ''}</script>`;
    }
    /**
     * Builds Usercentric script which can be inserted to the HEAD section of the website.
     * @param uc
     * @returns usercentric script when usercentric configration is defined and isIncluded is true else returns null
     */
    buildUsercentricsScript(uc) {
        return uc && this.isIncluded(uc) ? script(uc) : null;
    }
    /**
     * Builds google tag manager script which can be inserted to the HEAD section of the website
     * @param gtm
     * @returns Google Tag Manager when gtm configration is defined and isIcluded is true else returns null
     */
    buildGTMScript(gtm) {
        return gtm && this.isIncluded(gtm) ? gtmObject(gtm) : null;
    }
    /**
     * Builds gtag script which can be inserted to the HEAD section of the website
     * @param gtag
     * @returns Gtag script when gtag configration is defined and isIncluded is true else returns null
     */
    buildGtagScript(gtag) {
        return gtag && this.isIncluded(gtag) ? gtagObject(gtag) : null;
    }
    /**
     * Builds third party scripts that are not supported by GTM.
     * @param scripts
     * @returns custom script when custom script configration is defined and isIncluded is true else return empty array
     */
    buildOtherScripts(scripts) {
        if (!scripts) {
            return [];
        }
        return Object.entries(scripts)
            .filter(([, options]) => this.isIncluded(options))
            .map(([, options]) => {
            const serviceName = options.ucServiceName;
            return {
                type: ScriptTypes.PLAIN,
                'data-usercentrics': serviceName,
                ...omit(options, ['ucServiceName', 'includeIf']),
            };
        });
    }
    /**
     *
     * @param config
     * @returns true or false depending on the value passed to isIncluded. Defaults to true
     */
    isIncluded(config) {
        return config?.includeIf ?? true;
    }
    /**
     * Pushes any pending custom events to data layer
     */
    sendPendingCustomEventsToDatalayer() {
        if (this.customEvents.length) {
            this.customEvents.reverse().forEach((event) => window['dataLayer'].unshift(event));
            this.customEvents.length = 0;
        }
    }
    isEmptyObject(obj) {
        return obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype;
    }
}
const omit = (obj, keys) => {
    const objectClone = {};
    for (const prop of Reflect.ownKeys(obj).filter((v) => typeof v === 'string')) {
        if (keys.includes(prop)) {
            continue;
        }
        objectClone[prop] = obj[prop];
    }
    return objectClone;
};

function refreshSharedToken(container) {
    return (container.android?.refreshAuthToken() ||
        container.webkit?.messageHandlers.refreshAuthToken.postMessage(''));
}
function fireAppsflyerEvent(container, eventName, email) {
    const stringifiedParams = JSON.stringify({
        eventName,
        ...(email && { email }),
    });
    container.android?.recordAttributionEvent(stringifiedParams) ||
        container.webkit?.messageHandlers.recordAttributionEvent.postMessage({
            eventName,
            ...(email && { email }),
        });
}
function copyToClipboard(container, text) {
    if (container.android?.copyToClipboard) {
        container.android.copyToClipboard(text);
    }
    if (container.webkit?.messageHandlers?.copyToClipboard) {
        container.webkit?.messageHandlers?.copyToClipboard?.postMessage(text);
    }
}
function isClipboardAvailable(container) {
    return Boolean(container.android?.copyToClipboard || container.webkit?.messageHandlers?.copyToClipboard);
}
function fireFirebaseEvent(container, eventName, extras = {}) {
    try {
        const stringifiedParams = JSON.stringify({
            eventName: 'web_' + eventName,
            eventParams: extras,
        });
        container.android?.recordFirebaseEvent(stringifiedParams);
    }
    catch (e) { }
}
function closeWebview(container) {
    return container.android?.close() || container.webkit?.messageHandlers.close.postMessage('');
}
function userDataWasUpdated(container) {
    const dataUpdatedFunctionIsAvailable = (!!container.android && typeof container.android.dataUpdated === 'function') ||
        (!!container.webkit &&
            !!container.webkit.messageHandlers.dataUpdated &&
            typeof container.webkit.messageHandlers.dataUpdated.postMessage === 'function');
    if (dataUpdatedFunctionIsAvailable) {
        return (container.android?.dataUpdated() ||
            container.webkit?.messageHandlers.dataUpdated.postMessage(''));
    }
    return;
}
function setupSharedTokenCallback(container, callback) {
    if (!(typeof container.app === 'function' || (typeof container.app === 'object' && !!container.app))) {
        container.app = {};
    }
    if (!('setToken' in container.app)) {
        container.app.setToken = callback;
    }
    return container;
}
async function resolveSharedToken(getToken) {
    return new Promise(async (resolve) => {
        resolve(getToken() || '');
    });
}
function isWebview(container) {
    return !!container.android || !!container.webkit;
}
function navigateMobile(container, direction) {
    const action = direction === 'forward' ? 'didNavigateForward' : 'didNavigateBackward';
    return (container.webkit?.messageHandlers[action] &&
        container.webkit.messageHandlers[action].postMessage(''));
}
function setScrollable(container, scrollable) {
    return container.webkit?.messageHandlers.setScrollable.postMessage(scrollable);
}
function setBouncing(container, bouncing) {
    return container.webkit?.messageHandlers.setBouncing.postMessage(bouncing);
}
function getNavigationBarOffset(container) {
    return container.android?.safeAreaTopInset() || container.app?.safeAreaTopInset;
}
function setWebviewAsCloseable(container) {
    getFn(container, 'setWebviewCloseable', 'ios')();
    getFn(container, 'clearHistory', 'android')();
}
function isPushNotificationsEnabled(container) {
    return Boolean(container.app?.pushNotificationsEnabled);
}
function isLoyaltyNotificationsEnabled(container) {
    // in android all channels are active
    if (container.android)
        return true;
    return Boolean(container.app?.loyaltyPushEnabled);
}
function enableLoyaltyPushNotifications(container, value) {
    getFn(container, 'enableLoyaltyPushNotifications', 'ios')(value);
}
function getFn(container, fn, platform) {
    switch (platform) {
        case 'android':
            return (...args) => {
                container.android && container.android[fn](...args);
            };
        case 'ios':
            return (...args) => {
                const iosArgs = args.length ? args : [''];
                container.webkit?.messageHandlers[fn].postMessage(...iosArgs);
            };
        case 'all':
        default:
            return (...args) => {
                const iosArgs = args.length ? args : [''];
                container.webkit && container.webkit.messageHandlers[fn].postMessage(...iosArgs);
                container.android && container.android[fn](...args);
            };
    }
}

export { ScriptTypes, Tracking, closeWebview, copyToClipboard, enableLoyaltyPushNotifications, fireAppsflyerEvent, fireFirebaseEvent, getNavigationBarOffset, isClipboardAvailable, isLoyaltyNotificationsEnabled, isPushNotificationsEnabled, isWebview, navigateMobile, omit, refreshSharedToken, resolveSharedToken, setBouncing, setScrollable, setWebviewAsCloseable, setupSharedTokenCallback, userDataWasUpdated };
//# sourceMappingURL=index.es5.js.map
